import useQuery from "../../../../hooks/useQuery";
import { getDistinctValues, isEmpty, isFalse } from "../../../../utils/helpers";
import { getCutOrderLabel, getWhFlowInfoList } from "../../../../helpers/orderHelper";
import useSafeState from "../../../../hooks/useSafeState";

import useDeepEffect from "../../../../hooks/useDeepEffect";

import { useDeepMemo, useSetting, useTime } from "../../../../hooks";
import SettingKeys from "../../../../utils/SettingKeys";
import { FlowInfoFields } from "../../../../utils/entityFields";
import { format, formatDateTime } from "../../../../utils/dateUtils";
import useWarehouse from "../../../../hooks/useWarehouse";
import { InventoryStatus } from "../../../../utils/types";

const delivery = (flowInfo, utc_offset) =>
  utc_offset && !isEmpty(flowInfo.exFactoryDate) ? format(flowInfo.exFactoryDate, utc_offset, "dd MMM, yy") : null;

const keySeparator = "$@$";

const usePoView = props => {
  const orderId = props.match.params.id;
  const routeState = props.location?.state || {};

  const [version, setVersion] = useSafeState(0);
  const [selectedKey, setSelectedKey] = useSafeState(null);
  const [drawerState, setDrawerState] = useSafeState({ open: false, selectedRolls: [] });

  const { utc_offset } = useTime();

  const { allWhList, whList, whLoading } = useWarehouse();
  const whCodes = whList.map(e => e.code);

  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");
  const flowInfoValidation = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, "flowInfo");

  const { data: order = {} } = useQuery(orderId && { url: `orders/${orderId}`, params: { page: "wh-quality" }, version });
  const { data: ledgerData = [] } = useQuery(orderId && { url: `ledgers`, params: { search: `order.id==${orderId}`, size: 1000 }, version });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    const whFlowInfoList = getWhFlowInfoList(order);
    const rowList = whFlowInfoList
      .filter(fi => isEmpty(fi.parentId))
      .flatMap(parent => {
        const children = whFlowInfoList.filter(fi => parent.flowInfoIds.includes(fi.parentId));
        if (isEmpty(children)) {
          return parent.partList
            .sort((a, b) => a.serial - b.serial)
            .map(part => ({
              ...parent,
              key: `${part.effPartName}${keySeparator}${parent.flowInfoIds.join(",")}`,
              effPartName: part.effPartName,
              bomCu: part?.bomCu,
              uom: part?.uom,
              exFactoryDate: delivery(parent, utc_offset)
            }));
        } else {
          return [
            { ...parent, key: `${keySeparator}${parent.flowInfoIds.join(",")}`, exFactoryDate: delivery(parent, utc_offset) },
            ...children.flatMap(child =>
              child.partList
                .sort((a, b) => a.serial - b.serial)
                .map(part => ({
                  ...child,
                  key: `${part.effPartName}${keySeparator}${child.flowInfoIds.join(",")}`,
                  parentKey: `${keySeparator}${parent.flowInfoIds.join(",")}`,
                  effPartName: part.effPartName,
                  bomCu: part?.bomCu,
                  uom: part?.uom,
                  exFactoryDate: delivery(child, utc_offset)
                }))
            )
          ];
        }
      })
      .map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));

    return rowList;
  }, [order?.flowInfoList, utc_offset]);

  const selectedRow = flowInfoRows.find(e => e.key === selectedKey);

  useDeepEffect(() => {
    if (isEmpty(flowInfoRows)) return;
    // if (!isEmpty(routeState?.flowInfoId) && !isEmpty(routeState?.partId)) {
    //   setSelectedKey(`${routeState.flowInfoId}-${routeState.partId}`);
    // } else {
    //   setSelectedKey(flowInfoRows[0]?.key);
    // }
    const key = flowInfoRows.filter(f => !f.isParent)[0]?.key;
    setSelectedKey(key);
  }, [flowInfoRows, routeState]);

  // Columns and rows for Order flow table
  const flowInfoColumns = useDeepMemo(() => {
    if (isEmpty(flowInfoFields) || isEmpty(flowInfoValidation)) return [];

    let columns = [
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: FlowInfoFields.PRODUCT_ID, title: "Product", align: "left", render: (value, _, row) => row.product?.name, highlight: true },
      { dataKey: FlowInfoFields.RATIO, title: "Ratio", render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.STYLE, title: "Style", highlight: true },
      { dataKey: FlowInfoFields.COLOR, title: "Color", highlight: true },
      { dataKey: "effPartName", title: "Part", render: value => value || "-", filterOptions: { filter: true }, highlight: true },
      { dataKey: FlowInfoFields.ORDER_QTY, title: "Order Qty", align: "right" },
      { dataKey: FlowInfoFields.EXTRA, title: "Extra Ship (%)", align: "right", render: value => (isEmpty(value) ? "-" : `${value}%`) }
    ];

    if (flowInfoRows.every(e => isEmpty(e.ratio))) {
      columns = columns.filter(c => c.dataKey !== FlowInfoFields.RATIO);
    }

    return columns;
  }, [order, flowInfoFields, flowInfoValidation, flowInfoRows]);

  const { flowInfoIds, effPartName } = useDeepMemo(() => {
    const selectedRow = flowInfoRows.find(fi => fi.key === selectedKey);
    if (isEmpty(selectedRow)) return { flowInfoIds: [], effPartName: null };
    const [effPartName, flowInfoIds] = selectedRow.key?.split(keySeparator);
    return { flowInfoIds: flowInfoIds.split(",").map(id => Number(id)), effPartName };
  }, [flowInfoRows, selectedKey]);
  const flowInfoList = order?.flowInfoList?.filter(fi => flowInfoIds.includes(fi.id));

  const ledgerList = useDeepMemo(() => {
    if (isEmpty(flowInfoList) || whLoading) return [];
    const flowInfo = selectedRow || {};
    return ledgerData
      .filter(e => isEmpty(whCodes) || isEmpty(e.fabricItem?.warehouse) || whCodes.includes(e.fabricItem.warehouse))
      .filter(l => l.productId === flowInfo.product?.id && l.style === flowInfo.style && l.styleColor === flowInfo.color && l.part === effPartName);
  }, [flowInfoList, effPartName, ledgerData, whCodes, whLoading, selectedRow]);

  const fabricItemIds = getDistinctValues(ledgerList?.map(e => e.fabricItem?.id).filter(id => !isEmpty(id)));

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: "rolls",
      params: {
        search: `fabricItem.id=in=(${fabricItemIds.join(",")});invStatus=in=('${InventoryStatus.UNALLOCATED}', '${InventoryStatus.ALLOCATED}', '${
          InventoryStatus.PLANNED
        }','${InventoryStatus.ISSUED}','${InventoryStatus.DISABLED}');parentId=='NULL'`,
        size: 10000
      },
      version
    }
  );

  const { data: whLedgerList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: "ledgers",
      params: { search: `fabricItem.id=in=(${fabricItemIds.join(",")});type=='WH_TRANSFER'`, size: 10000 },
      version
    }
  );

  const invFabricItemIds = getDistinctValues(rollInfoList?.map(ri => ri.fabricItemId) || []);
  const { data: fabricItemList = [] } = useQuery(
    !isEmpty(invFabricItemIds) && { url: `fabric-items`, params: { search: `id=in=(${invFabricItemIds.join(",")})` } }
  );

  const rollList = useDeepMemo(() => {
    if (isEmpty(rollInfoList) || isEmpty(fabricItemList)) return [];
    const rollList = rollInfoList
      .filter(ri => fabricItemIds.includes(ri.fabricItemId))
      .map(rollInfo => {
        const fabricItem = fabricItemList?.find(e => e.id === rollInfo.fabricItemId) || {};
        const cutOrder = order?.orderFlowList?.find(e => e.id === rollInfo.cutOrderId);

        return {
          ...rollInfo,
          ...fabricItem,
          extRemarks: rollInfo?.extRemarks || "",
          fabricItem,
          id: rollInfo.id,
          itemCode: fabricItem?.fabric?.itemCode,
          invoiceNo: fabricItem.invoice?.invoiceNo,
          grnWarehouse: allWhList?.find(w => w.code === fabricItem.warehouse)?.name || fabricItem.warehouse,
          currWarehouse: allWhList?.find(w => w.code === rollInfo.currWarehouse)?.name || rollInfo.currWarehouse,
          grnWarehouseCode: fabricItem.warehouse,
          currWarehouseCode: rollInfo.currWarehouse,
          cutOrder: getCutOrderLabel(cutOrder, order, ["poRef", "color", "lot"])
        };
      });
    return rollList;
  }, [rollInfoList, fabricItemIds, fabricItemList, allWhList]);

  const whTxnRows = useDeepMemo(() => {
    if (isEmpty(whLedgerList)) return [];
    return whLedgerList
      .sort((a, b) => b.id - a.id)
      .map(ledger => {
        return {
          ...ledger,
          externalId: !isEmpty(ledger.externalIds) ? JSON.parse(ledger.externalIds)[0]?.externalId : "",
          fromWarehouse: allWhList?.find(w => w.code === ledger.fromWarehouse)?.name || ledger.fromWarehouse,
          toWarehouse: allWhList?.find(w => w.code === ledger.toWarehouse)?.name || ledger.toWarehouse,
          invoiceId: ledger.invoice?.id,
          invoiceNo: ledger.invoice?.invoiceNo,
          grn: ledger.fabricItem?.grn,
          fabricId: ledger.fabric?.id,
          itemCode: ledger.fabric?.itemCode,
          itemDesc: ledger.fabric?.itemCode,
          effFabColor: ledger.fabricItem?.effFabColor,
          colorShade: ledger.fabricItem?.colorShade,
          fabricPo: ledger.fabricItem?.fabricPo,
          orderingWidth: ledger.fabricItem?.orderingWidth,
          uomWidth: ledger.fabricItem?.uomWidth,
          uomLength: ledger.fabricItem?.uomLength,
          transferredQty: ledger.qty,
          time: !isEmpty(utc_offset) ? formatDateTime(ledger.createdAt, utc_offset) : "-",
          user: ledger.createdBy
        };
      });
  }, [whLedgerList, allWhList, utc_offset]);

  const onRowClick = row => {
    if (isFalse(row.isParent)) {
      setSelectedKey(row.key);
    }
  };

  const onRefresh = () => {
    setVersion(v => v + 1);
  };

  return {
    version,
    selectedKey,
    flowInfoColumns,
    flowInfoRows,
    whTxnRows,
    rollList,
    allWhList,
    drawerState,
    setDrawerState,
    onRowClick,
    onRefresh
  };
};

export default usePoView;
